import {
  Badge,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import type {
  SearchFilterConditionCustomAttribute,
  SearchFilterConditionCustomAttributeField,
  SearchFilterOperator,
  SearchFilterOperatorExistence,
  SearchFilterOperatorString,
  SearchFilterValueStringList,
} from 'lib-search-types';
import {
  filterOperatorCatalogue,
  SearchFilterLayout,
  SearchFilterOptions,
} from 'lib-search-ui';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  CustomFieldSelectAttribute,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { convertTreeToFlatItems } from 'utils/tree';

import styles from './searchCustomAttributesFilterSelect.module.scss';

type SearchCustomAttributesFilterSelectProps = {
  field: SearchFilterConditionCustomAttributeField;
  attribute: CustomFieldSelectAttribute;
  condition: SearchFilterConditionCustomAttribute | undefined;
  onChange: (
    condition: SearchFilterConditionCustomAttribute | null,
    attributeId: string,
  ) => void;
  onBack: () => void;
};

export function SearchCustomAttributesFilterSelect({
  field,
  attribute,
  attribute: { options },
  condition,
  onChange,
  onBack,
}: SearchCustomAttributesFilterSelectProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    inputRef.current?.focus();
  }, []);

  const selectedOperator = (condition?.operator ?? 'is_one') as
    | SearchFilterOperatorString
    | SearchFilterOperatorExistence;

  const selectedValues = useMemo((): SearchFilterValueStringList[] => {
    if (!condition?.values) return [];

    return condition.values.map(({ value }) => ({
      value,
    }));
  }, [condition?.values]);

  const isExistenceOperator =
    selectedOperator === 'is_empty' || selectedOperator === 'is_not_empty';

  // Leaf-only flat options with full path labels, used for search.
  const flatOptions = useMemo(
    () =>
      convertTreeToFlatItems(options ?? [], {
        childrenKey: 'options',
        transformItem: (option, path) => ({
          ...option,
          label: path
            .map(({ label }) => label)
            .concat([option.label])
            .join(' / '),
          value: path.map(({ value }) => value).concat([option.value]),
        }),
      }).filter(({ options }) => !options?.length),
    [options],
  );

  const handleChange = useCallback(
    ({
      selectedValues: newValues,
      selectedItem,
    }: SelectionListChangeEvent<SearchFilterValueStringList>) => {
      // When selecting a node, remove any previously selected child values.
      const values =
        selectedItem.isNode && selectedItem.isSelected
          ? newValues.filter((v) => {
              const isChild =
                v.value.length > selectedItem.value.value.length &&
                intersection(v.value, selectedItem.value.value).length > 0;
              return !isChild;
            })
          : newValues;

      if (values.length === 0) {
        onChange(null, attribute.id);
      } else {
        onChange(
          {
            field,
            operator: selectedOperator,
            values,
            attributeId: attribute.id,
          } as SearchFilterConditionCustomAttribute,
          attribute.id,
        );
      }
    },
    [attribute.id, field, onChange, selectedOperator],
  );

  const handleChangeOperator = useCallback(
    (newOperator: SearchFilterOperator) => {
      const isNewExistence =
        newOperator === 'is_empty' || newOperator === 'is_not_empty';

      onChange(
        {
          field,
          operator: newOperator,
          values: isNewExistence ? [] : selectedValues,
          attributeId: attribute.id,
        } as SearchFilterConditionCustomAttribute,
        attribute.id,
      );
    },
    [attribute.id, field, onChange, selectedValues],
  );

  const handleClear = useCallback(() => {
    onChange(null, attribute.id);
  }, [attribute.id, onChange]);

  const renderFilteredListItems = useCallback(() => {
    const fuse = new Fuse(flatOptions, {
      keys: ['label', 'value'],
      threshold: 0.2,
      distance: 200,
    });

    const filteredOptions = fuse
      .search(debouncedSearchTerm)
      .map(({ item }) => item);

    if (!filteredOptions.length) return <SearchFilterLayout.Empty />;

    return filteredOptions.map((option) => (
      <SearchFilterLayout.Item
        key={option.id}
        value={{ value: option.value }}
        label={option.label}
        highlight={debouncedSearchTerm}
        isDisabled={option.is_deactivated}
      />
    ));
  }, [debouncedSearchTerm, flatOptions]);

  const renderListOrItem = useCallback(
    (
      option: CustomFieldSelectAttributeOption,
      parentValuePath: readonly string[] = [],
    ) => {
      const valuePath = [...parentValuePath, option.value];

      if (option.options?.length) {
        const isSelected = selectedValues.some((v) =>
          isEqual(v.value, valuePath),
        );
        const selectedChildValues = selectedValues.filter(
          (v) =>
            v.value.length > valuePath.length &&
            isEqual(v.value.slice(0, valuePath.length), valuePath),
        );

        const parentItem = (
          <SearchFilterLayout.Item
            key={option.id}
            value={{ value: valuePath }}
            label={option.label}
            isDisabled={isExistenceOperator}
            isIndeterminate={selectedChildValues.length > 0}
            contentClassName={styles.itemContent}
          >
            {selectedChildValues.length > 0 && (
              <Badge variant="secondary" size="small">
                {selectedChildValues.length}
              </Badge>
            )}
          </SearchFilterLayout.Item>
        );

        // If the parent item is selected, don't render the children.
        if (isSelected) return parentItem;

        return (
          <SelectionList.List key={option.id} parentItem={parentItem}>
            {[...option.options].map((child) =>
              renderListOrItem(child, valuePath),
            )}
          </SelectionList.List>
        );
      }

      return (
        <SearchFilterLayout.Item
          key={option.id}
          value={{ value: valuePath }}
          label={option.label}
          isDisabled={isExistenceOperator || option.is_deactivated}
          textProps={option.is_deactivated ? { color: 'tertiary' } : undefined}
        />
      );
    },
    [isExistenceOperator, selectedValues],
  );

  const renderListItems = useCallback(
    () => (options ?? []).map((option) => renderListOrItem(option)),
    [options, renderListOrItem],
  );

  return (
    <>
      <SearchFilterLayout.Row borderBottom>
        <InputText
          ref={inputRef}
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search values..."
          disabled={isExistenceOperator}
          suffix={{
            type: 'custom',
            value: (
              <FlexBox alignItems="center" padding="0 100">
                <IconButton
                  variant="tertiary"
                  icon={searchTerm ? 'close' : 'search'}
                  onClick={searchTerm ? handleClearSearch : undefined}
                />
              </FlexBox>
            ),
            noWrap: true,
          }}
          autoFocus
        />
      </SearchFilterLayout.Row>

      <SearchFilterLayout.Row>
        <Text.Paragraph variant="3" subdued>
          {attribute.label}
        </Text.Paragraph>
      </SearchFilterLayout.Row>

      <SearchFilterLayout.Row noPadding scrollable>
        <SelectionList
          isMultiple
          isActive
          selectedValues={selectedValues}
          onChange={handleChange}
          equalityFn={isEqual}
          defaultActiveItemIndex={1}
        >
          <SelectionList.BackItem onSelected={onBack} />

          {debouncedSearchTerm ? renderFilteredListItems() : renderListItems()}
        </SelectionList>
      </SearchFilterLayout.Row>

      <SearchFilterLayout.Row borderTop>
        <SearchFilterOptions
          operators={
            filterOperatorCatalogue.users.contact_custom_attribute.operators
          }
          selectedOperator={selectedOperator}
          defaultOperator={
            filterOperatorCatalogue.users.contact_custom_attribute
              .defaultOperator
          }
          isMissingSelectedValues={!selectedValues.length}
          onChangeOperator={handleChangeOperator}
          onClear={handleClear}
        />
      </SearchFilterLayout.Row>
    </>
  );
}
